export const range = (start: number, end: number) => {
  return Array.from({ length: end - start }, (_, value) => value + start);
};

export const rangeInclude = (start: number, end: number) => range(start, end + 1);

type Placer<TItem, TResult> = (targetItem: TItem, index: number, target: TItem[]) => TResult;

export const reduceToMap = <TItem, TKey, TValue>(
  target: TItem[],
  keyPlacer: Placer<TItem, TKey>,
  valuePlacer: Placer<TItem, TValue>,
  initialValue?: Map<TKey, TValue>,
): Map<TKey, TValue> => {
  return target.reduce((map, targetItem, index, target) => {
    map.set(keyPlacer(targetItem, index, target), valuePlacer(targetItem, index, target));
    return map;
  }, initialValue ?? new Map<TKey, TValue>());
};

export const reduceToArrayMap = <TItem, TKey, TValue>(
  target: TItem[],
  keyPlacer: Placer<TItem, TKey>,
  valuePlacer: Placer<TItem, TValue>,
): Map<TKey, TValue[]> => {
  return target.reduce((map, targetItem, index, target) => {
    const key = keyPlacer(targetItem, index, target);
    const value = valuePlacer(targetItem, index, target);

    if (!map.has(key)) map.set(key, []);
    map.get(key)?.push(value);
    return map;
  }, new Map<TKey, TValue[]>());
};

export const reduceToSetMap = <TItem, TKey, TValue>(
  target: TItem[],
  keyPlacer: Placer<TItem, TKey>,
  valuePlacer: Placer<TItem, TValue>,
): Map<TKey, Set<TValue>> => {
  return target.reduce((map, targetItem, index, target) => {
    const key = keyPlacer(targetItem, index, target);
    const value = valuePlacer(targetItem, index, target);

    if (!map.has(key)) map.set(key, new Set());
    map.get(key)?.add(value);
    return map;
  }, new Map<TKey, Set<TValue>>());
};

/**
 * 객체 배열을 정렬할 때 쓸 비교 함수를 만들어 반환한다.
 *
 * @param retriever 객체로부터 비굣값을 추출하는 함수.
 * @param desc 내림차순 정렬 여부.
 */
export function orderBy<T>(retriever: (v: T) => number, desc?: boolean): (a: T, b: T) => number {
  return desc
    ? (rhs, lhs) => retriever(lhs) - retriever(rhs)
    : (lhs, rhs) => retriever(lhs) - retriever(rhs);
}

export function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'number');
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}
